import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import "./CheckoutPage.css";
import Loader from "../layouts/components/Loader";
import { cartAPI, addressAPI, checkoutAPI } from "../utils/zone-api";

const API_BASE = "https://api.mariamlyluxe.com/public";

const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("auth_token") ||
  localStorage.getItem("access_token") ||
  "";

/** Always uses latest token */
async function apiFetch(path, { method = "GET", body } = {}) {
  const headers = { Accept: "application/json" };
  const tok = getToken();
  if (tok) headers.Authorization = `Bearer ${tok}`;
  const opts = { method, headers };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE}${path}`, opts);
  let data = null;
  try {
    data = await res.json();
  } catch {}
  if (!res.ok) {
    const err = new Error(data?.message || `Request failed: ${res.status}`);
    err.status = res.status;
    err.errors = data?.errors;
    throw err;
  }
  return data;
}

function formatPrice(n) {
  const val = Number(n) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(val);
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const authed = !!getToken();
  
  // Cart data
  const [cartItems, setCartItems] = useState([]);
  const [cartSummary, setCartSummary] = useState({ subtotal: 0, shipping: 0, total: 0 });
  const [cartLoading, setCartLoading] = useState(true);
  const [cartError, setCartError] = useState("");
  
  // Address data
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressesError, setAddressesError] = useState("");
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  
  // Order processing
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState("");
  
  // Load cart data
  const loadCart = async (addressId = null) => {
    if (!authed) {
      setCartError("You must be logged in to checkout.");
      setCartLoading(false);
      return;
    }
    
    setCartLoading(true);
    setCartError("");
    try {
      const data = await cartAPI.getCart(getToken(), addressId);
      
      if (data.success) {
        const items = data.items || [];
        setCartItems(items);
        
        const summary = data.summary || {};
        setCartSummary({
          subtotal: Number(summary.subtotal) || 0,
          shipping: Number(summary.shipping) || 0,
          total: Number(summary.total) || 0
        });
      } else {
        setCartError("Failed to load cart.");
        setCartItems([]);
      }
    } catch (e) {
      setCartError(e.message || "Failed to load cart.");
      setCartItems([]);
    } finally {
      setCartLoading(false);
    }
  };
  
  // Load addresses
  const loadAddresses = async () => {
    if (!authed) return;
    
    setAddressesLoading(true);
    setAddressesError("");
    try {
      const data = await addressAPI.getAddresses(getToken());
      if (data.success) {
        const addressList = data.addresses || [];
        setAddresses(addressList);
        
        // Auto-select first address if available
        if (addressList.length > 0 && !selectedAddressId) {
          setSelectedAddressId(addressList[0].id);
        }
      } else {
        setAddresses([]);
      }
    } catch (e) {
      setAddressesError(e.message || "Failed to load addresses.");
      setAddresses([]);
    } finally {
      setAddressesLoading(false);
    }
  };
  
  useEffect(() => {
    loadCart();
    loadAddresses();
  }, [authed]);

  // Reload cart with shipping when address changes
  useEffect(() => {
    if (selectedAddressId) {
      loadCart(selectedAddressId);
    }
  }, [selectedAddressId]);
  
  // Handle address selection
  const handleAddressSelect = (addressId) => {
    setSelectedAddressId(addressId);
  };

  // Handle order placement
  const handlePlaceOrder = async () => {
    if (!selectedAddressId) {
      setOrderError("Please select a delivery address.");
      return;
    }
    
    if (cartItems.length === 0) {
      setOrderError("Your cart is empty.");
      return;
    }
    
    setOrderLoading(true);
    setOrderError("");
    
    try {
      const orderData = {
        address_id: selectedAddressId,
        payment_code: "cash"
      };
      
      const result = await checkoutAPI.processCheckout(orderData, getToken());
      
      if (result.success) {
        // Show success toast with message from response
        const successMessage = result.message || "Order placed successfully! Thank you for your purchase.";
        toast.success(successMessage);
        
        // Redirect to account page after a short delay to show the toast
        setTimeout(() => {
          navigate("/account?tab=orders");
        }, 1500);
      } else {
        setOrderError(result.message || "Failed to place order. Please try again.");
      }
      
    } catch (e) {
      setOrderError(e.message || "Failed to place order.");
    } finally {
      setOrderLoading(false);
    }
  };
  
  // Redirect if not authenticated
  if (!authed) {
    return (
      <section className="checkout-page">
        <div className="checkout-container">
          <h1>Checkout</h1>
          <div className="checkout-error">
            <p>You must be logged in to checkout.</p>
            <Link to="/account" className="checkout-btn checkout-btn-primary">
              Login / Register
            </Link>
          </div>
        </div>
      </section>
    );
  }
  
  // Show loading state
  if (cartLoading) {
    return (
      <section className="checkout-page">
        <div className="checkout-container">
          <h1>Checkout</h1>
          <Loader />
        </div>
      </section>
    );
  }
  
  // Show error state
  if (cartError) {
    return (
      <section className="checkout-page">
        <div className="checkout-container">
          <h1>Checkout</h1>
          <div className="checkout-error">
            <p>{cartError}</p>
            <Link to="/cart" className="checkout-btn checkout-btn-outline">
              Back to Cart
            </Link>
          </div>
        </div>
      </section>
    );
  }
  
  // Show empty cart
  if (cartItems.length === 0) {
    return (
      <section className="checkout-page">
        <div className="checkout-container">
          <h1>Checkout</h1>
          <div className="checkout-empty">
            <p>Your cart is empty.</p>
            <Link to="/shop" className="checkout-btn checkout-btn-primary">
              Continue Shopping
            </Link>
          </div>
        </div>
      </section>
    );
  }
  
  const selectedAddress = addresses.find(addr => addr.id === selectedAddressId);
  
  return (
    <section className="checkout-page">
      <div className="checkout-container">
        <h1>Checkout</h1>
        
        <div className="checkout-content">
          {/* Address Selection */}
          <div className="checkout-section">
            <h2>Delivery Address</h2>
            
            {addressesLoading ? (
              <Loader />
            ) : addressesError ? (
              <div className="checkout-error">
                <p>{addressesError}</p>
                <Link to="/account" className="checkout-btn checkout-btn-outline">
                  Manage Addresses
                </Link>
              </div>
            ) : addresses.length === 0 ? (
              <div className="checkout-no-address">
                <p>No addresses found. Please add an address to continue.</p>
                <Link to="/account" className="checkout-btn checkout-btn-primary">
                  Add Address
                </Link>
              </div>
            ) : (
              <div className="checkout-addresses">
                {addresses.map((address) => (
                  <div
                    key={address.id}
                    className={`checkout-address-item ${
                      selectedAddressId === address.id ? "selected" : ""
                    }`}
                    onClick={() => handleAddressSelect(address.id)}
                  >
                    <div className="checkout-address-radio">
                      <input
                        type="radio"
                        name="address"
                        value={address.id}
                        checked={selectedAddressId === address.id}
                        onChange={() => handleAddressSelect(address.id)}
                      />
                    </div>
                    <div className="checkout-address-content">
                      <div className="checkout-address-header">
                        <h3>{address.first_name} {address.last_name}</h3>
                        <span className="checkout-address-phone">{address.phone_number}</span>
                      </div>
                      <div className="checkout-address-details">
                        <p>{address.full_address}</p>
                        {address.more_details && (
                          <p className="checkout-address-extra">{address.more_details}</p>
                        )}
                        <span className="checkout-address-zone">
                          Zone: {address.zone?.name || `Zone ${address.zone_id}`}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="checkout-address-actions">
                  <Link to="/account" className="checkout-btn checkout-btn-outline">
                    Manage Addresses
                  </Link>
                </div>
              </div>
            )}
          </div>
          
          {/* Order Summary */}
          <div className="checkout-section">
            <h2>Order Summary</h2>
            
            <div className="checkout-items">
              {cartItems.map((item, index) => (
                <div key={index} className="checkout-item">
                  <div className="checkout-item-info">
                    <h4>{item.name || item.product?.name || "Product"}</h4>
                    <p>Quantity: {item.quantity || item.qty || item.pivot?.quantity || 1}</p>
                  </div>
                  <div className="checkout-item-price">
                    {formatPrice((item.price || item.selling_price || item.product?.price || item.product?.selling_price || 0) * (item.quantity || item.qty || item.pivot?.quantity || 1))}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="checkout-summary">
              <div className="checkout-summary-row">
                <span>Subtotal</span>
                <span>{formatPrice(cartSummary.subtotal)}</span>
              </div>
              <div className="checkout-summary-row">
                <span>Shipping</span>
                <span>
                  {selectedAddress && selectedAddress.zone ? (
                    <>
                      {formatPrice(cartSummary.shipping)}
                      <small className="shipping-zone-info">
                        ({selectedAddress.zone.name})
                      </small>
                    </>
                  ) : (
                    "Select address"
                  )}
                </span>
              </div>
              <div className="checkout-summary-row checkout-total">
                <span>Total</span>
                <span>{formatPrice(cartSummary.total || cartSummary.subtotal)}</span>
              </div>
            </div>
            
            {orderError && (
              <div className="checkout-error">
                <p>{orderError}</p>
              </div>
            )}
            
            <button
              className="checkout-btn checkout-btn-primary checkout-place-order"
              onClick={handlePlaceOrder}
              disabled={!selectedAddressId || orderLoading}
            >
              {orderLoading ? <Loader /> : "Place Order"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
